import React, { useState, useRef, useEffect } from "react";
import { useAuth } from "react-oidc-context";
import { ThreeDots } from "react-loader-spinner";
import "./multiLocSearchPanel.css";
import { useNitroStore } from "../../../store/Store";
import { useNitroCAStore } from "../../../store/CompetitiveAnalysisStore";
import NitroService from "../../../utils/nitro.service";
import { IGetQuotesParams } from "../../../types/IQuotes";
import { useQuotesForDestinations } from "../../../hooks/useQuotesForDestinations";
import { icons } from "../../../svg";

interface SearchResult {
  label: string;
  value: string;
  type: string;
}

interface MultiLocSearchPanelProps {
  onCancel: () => void;
  onResultClick: (
    searchValue: string,
    searchType: "location" | "company",
    destinationQuotes?: any[],
    surroundingLocations?: any[],
  ) => void;
  selectedMonth: string;
  selectedProduct: string;
  initialQuery?: string;
  onSearchTextChange?: (text: string) => void;
}

const MultiLocSearchPanel: React.FC<MultiLocSearchPanelProps> = ({
  onCancel,
  onResultClick,
  selectedMonth,
  selectedProduct,
  initialQuery = "",
  onSearchTextChange,
}) => {
  const [
    priorityProducts,
    currency,
    uom,
    multiLocSearchType,
    setMultiLocSearchType,
  ] = useNitroStore((state: any) => [
    state.priorityProducts,
    state.currency,
    state.uom,
    state.multiLocSearchType,
    state.setMultiLocSearchType,
  ]);
  const refetchQuotes = useNitroCAStore((state) => state.refetchQuotes);

  const auth = useAuth();
  const user = `${auth.user?.profile.given_name ?? ""} ${
    auth.user?.profile.family_name ?? ""
  }`.trim();

  const searchType = multiLocSearchType;
  const setSearchType = setMultiLocSearchType;
  const [searchText, setSearchText] = useState(initialQuery);
  const inputRef = useRef<HTMLInputElement>(null);
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [panelReady, setPanelReady] = useState(false);
  const [selectedResult, setSelectedResult] = useState<SearchResult | null>(
    null,
  );
  const [localRefetchTrigger, setLocalRefetchTrigger] = useState(0);
  const [isWaitingForPanel, setIsWaitingForPanel] = useState(false);

  useEffect(() => {
    setSearchText(initialQuery);
  }, [initialQuery]);

  const [viewport, setViewport] = useState({
    height: window.innerHeight,
    top: 0,
  });

  useEffect(() => {
    document.documentElement.classList.add("search-panel-active");
    document.body.classList.add("search-panel-active");

    const handleViewport = () => {
      if (window.visualViewport) {
        setViewport({
          height: window.visualViewport.height,
          top: window.visualViewport.offsetTop,
        });
        window.scrollTo(0, 0);
      }
    };

    window.visualViewport?.addEventListener("resize", handleViewport);
    window.visualViewport?.addEventListener("scroll", handleViewport);
    handleViewport();

    return () => {
      document.documentElement.classList.remove("search-panel-active");
      document.body.classList.remove("search-panel-active");
      window.visualViewport?.removeEventListener("resize", handleViewport);
      window.visualViewport?.removeEventListener("scroll", handleViewport);
    };
  }, []);

  let quoteId = "";
  if (searchType === "location" && selectedResult) {
    quoteId = selectedResult.value;
  }

  const quoteParams: IGetQuotesParams = {
    id: quoteId,
    product: selectedProduct || "AMMONIA",
    party:
      searchType === "company" && selectedResult ? selectedResult.value : null,
    marketer: user,
    shipDate: selectedMonth,
    currency: currency || "USD",
    uom: uom || "ST",
  };

  const combinedRefetchTrigger = localRefetchTrigger + refetchQuotes;
  // Both hooks are always called (rules of hooks); the inactive one is disabled and skips its fetch.
  const quotesResult = useQuotesForDestinations(
    quoteParams,
    combinedRefetchTrigger,
  );
  const { quotes, loading: multiLocLoading } = quotesResult;

  useEffect(() => {
    if (priorityProducts?.length > 0) setPanelReady(true);
  }, [priorityProducts]);

  useEffect(() => {
    if (searchText.length === 0) {
      setSearchResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const results =
          searchType === "company"
            ? await NitroService.getPartiesLike(searchText, user)
            : await NitroService.getLocationExpanded(searchText);

        setSearchResults(
          results.map((r: any) => ({
            label: r.name || "",
            value: r.name || "",
            type: (r.type || "COMPANY").toUpperCase(),
          })),
        );
      } catch (e) {
        console.error(e);
      }
      setLoading(false);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchText, searchType, user]);

  const handleResultClick = (result: SearchResult) => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    setIsWaitingForPanel(true);
    setSelectedResult(result);
    setLocalRefetchTrigger((prev) => prev + 1);
  };

  useEffect(() => {
    if (selectedResult && combinedRefetchTrigger > 0 && !multiLocLoading) {
      setIsWaitingForPanel(false);
      setSelectedResult(null);
      onResultClick(selectedResult.value, searchType, quotes, []);
    }
  }, [
    quotes,
    multiLocLoading,
    selectedResult,
    combinedRefetchTrigger,
    onResultClick,
    searchType,
  ]);

  return (
    <>
      {(loading || multiLocLoading || isWaitingForPanel) && (
        <div className="loader-overlay">
          <ThreeDots width={120} color="#fff" />
        </div>
      )}

      <div
        className="search-panel-container"
        style={{
          height: `${viewport.height}px`,
          transform: `translateY(${viewport.top}px)`,
        }}
      >
        <div className="header-section">
          <div className="close-header">
            <button
              type="button"
              onClick={() => {
                if (document.activeElement instanceof HTMLElement) {
                  document.activeElement.blur();
                }
                onCancel();
              }}
              className="icon-btn"
            >
              <img src={icons.CircleCloseIcon} alt="Close" />
            </button>
          </div>

          <div id="search-by-toggle-container" className="toggle-container">
            <div
              className={`toggle-bg ${
                searchType === "company" ? "right" : "left"
              }`}
            />
            <button
              id="search-by-location-toggle-id"
              type="button"
              className={`toggle-btn ${
                searchType === "location" ? "active" : ""
              }`}
              onClick={() => setSearchType("location")}
            >
              Location <span className="sub-text">Single Location</span>
            </button>
            <button
              id="search-by-company-toggle-id"
              type="button"
              className={`toggle-btn ${
                searchType === "company" ? "active" : ""
              }`}
              onClick={() => setSearchType("company")}
            >
              Company <span className="sub-text">Multiple Locations</span>
            </button>
          </div>

          <div className="search-row-wrapper">
            <form
              action="/"
              onSubmit={(e) => {
                e.preventDefault();
                inputRef.current?.blur();
              }}
              className="search-bar-row"
            >
              <div id="search-input-container" className="input-wrapper">
                <input
                  id="search-input-panel"
                  ref={inputRef}
                  type="text"
                  inputMode="search"
                  enterKeyHint="search"
                  autoFocus={Boolean(initialQuery)} // eslint-disable-line jsx-a11y/no-autofocus
                  style={{ fontSize: "16px" }}
                  placeholder={
                    searchType === "company"
                      ? "Search a Company"
                      : "Search a City or Facility Name"
                  }
                  value={searchText}
                  onChange={(e) => {
                    const newText = e.target.value;
                    setSearchText(newText);
                    if (onSearchTextChange) {
                      onSearchTextChange(newText);
                    }
                  }}
                />
                {searchText && (
                  <button
                    id="mobile-search-clear-btn"
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      setSearchText("");
                      if (onSearchTextChange) {
                        onSearchTextChange("");
                      }
                    }}
                    onTouchStart={(e) => {
                      e.preventDefault();
                      setSearchText("");
                      if (onSearchTextChange) {
                        onSearchTextChange("");
                      }
                    }}
                  >
                    <img
                      id="mobile-circle-close-icon"
                      src={icons.CircleCloseIcon}
                      alt="Clear"
                    />
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>

        <div className="content-scroll-area">
          <div className="results-list">
            {searchText.length > 0 &&
              panelReady &&
              searchResults.length === 0 && (
                <div className="no-results">No results found</div>
              )}
            {searchText.length > 0 &&
              panelReady &&
              searchResults.length > 0 &&
              searchResults.map((result) => (
                <button
                  key={result.value}
                  type="button"
                  className="result-item"
                  onClick={() => handleResultClick(result)}
                >
                  <div className="result-main">{result.label}</div>
                  <div className="result-sub">{result.type}</div>
                </button>
              ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default MultiLocSearchPanel;
